import React, { useEffect, useState } from "react";
import FeatureCardContent from "@shared/FeatureCardContent/FeatureCardContent";
import { getSeasonalTheme } from "@lib/seasonalTheme";
import "./RevenueMetricsCard.css";

const metricTargets = [
  { label: "MRR", prefix: "$", suffix: "K", target: 118.4, decimals: 1, delta: "+8.2% MoM" },
  { label: "ARR", prefix: "$", suffix: "M", target: 1.42, decimals: 2, delta: "+31% YoY" },
  { label: "GRR", prefix: "", suffix: "%", target: 96.5, decimals: 1, delta: "+0.4 pts" },
];

const sparkPoints = [18, 22, 21, 27, 30, 29, 35, 38, 36, 42, 47, 51];

function useCountTo(target, decimals, durationMs = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / durationMs, 1);
      const eased = 1 - (1 - t) ** 3;
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value.toFixed(decimals);
}

function MetricTile({ metric, delay }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setArmed(true), delay);
    return () => clearTimeout(id);
  }, [delay]);
  const display = useCountTo(armed ? metric.target : 0, metric.decimals, 1100);
  return (
    <div className="revenue-mock__metric">
      <div className="revenue-mock__metric-value">
        {metric.prefix}
        {display}
        {metric.suffix}
      </div>
      <div className="revenue-mock__metric-label">{metric.label}</div>
      <div className="revenue-mock__metric-delta">{metric.delta}</div>
    </div>
  );
}

function Sparkline() {
  const w = 100;
  const h = 32;
  const pad = 2;
  const max = Math.max(...sparkPoints);
  const min = Math.min(...sparkPoints);
  const span = Math.max(1, max - min);
  const coords = sparkPoints.map((v, i) => ({
    x: pad + (i / (sparkPoints.length - 1)) * (w - pad * 2),
    y: pad + (h - pad * 2) * (1 - (v - min) / span),
  }));
  const points = coords.map((c) => `${c.x},${c.y}`).join(" ");
  const area = `${points} ${w - pad},${h} ${pad},${h}`;
  const last = coords[coords.length - 1];

  return (
    <div className="revenue-mock__spark-wrap">
      <svg
        className="revenue-mock__spark"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Recurring revenue trending up over the trailing period"
      >
        <defs>
          <linearGradient id="revenue-spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-success)" stopOpacity="0.24" />
            <stop offset="100%" stopColor="var(--color-success)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon className="revenue-mock__spark-area" points={area} fill="url(#revenue-spark-fill)" />
        <polyline
          className="revenue-mock__spark-line"
          points={points}
          pathLength="1"
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="revenue-mock__spark-dot"
        style={{ left: `${(last.x / w) * 100}%`, top: `${(last.y / h) * 100}%` }}
        aria-hidden="true"
      />
    </div>
  );
}

function RevenueMetricsCard() {
  const theme = getSeasonalTheme();
  return (
    <article
      className="venture-card revenue-card"
      style={{
        "--card-monet": `url(${theme.paintings.revenue})`,
        "--card-monet-position": "center 65%",
      }}
    >
      <FeatureCardContent
        className="feature-card-content--startup-card"
        badgeTitle="Revenue engineering"
        title="Revenue metrics investors can verify, not just read."
        text="Vienna pulls MRR, ARR, and retention straight from Stripe, so due diligence, deal ops, and founder updates all work off the same verified numbers."
        icon={<span className="feature-card-content__glyph">$</span>}
      >
        <div className="feature-card-content__tags">
          <span className="feature-card-content__tag">MRR / ARR</span>
          <span className="feature-card-content__tag">GRR &amp; cohorts</span>
          <span className="feature-card-content__tag">Stripe-verified</span>
        </div>
      </FeatureCardContent>

      <div className="venture-card__pane venture-card__pane--visual">
        <div className="venture-mock revenue-mock">
          <div className="venture-mock__header">
            <span>Revenue - Apex Robotics</span>
            <span className="revenue-mock__live">
              <span className="revenue-mock__live-dot" />
              Stripe-synced
            </span>
          </div>
          <div className="venture-mock__body revenue-mock__body">
            <div className="revenue-mock__metrics">
              {metricTargets.map((m, i) => (
                <MetricTile key={m.label} metric={m} delay={150 + i * 180} />
              ))}
            </div>
            <div className="revenue-mock__chart">
              <span className="revenue-mock__chart-label">MRR - trailing 12mo</span>
              <Sparkline />
            </div>
            <p className="revenue-mock__caption">
              Reconciled directly from live Stripe subscriptions - no manual exports.
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

export default RevenueMetricsCard;
